import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/header';
import PageMenu from '../components/pagemenu';
import { useUserContext } from '../components/UserContext';
import InquiryDetail from '../components/inbox/InquiryDetail';
import NewInquiryForm from '../components/inbox/NewInquiryForm';
import { fetchCrmSummary, fetchFollowUps, fetchInquiries } from '../components/api';
import { formatShortDate, KIND_OPTIONS, statusClass } from '../components/inbox/inboxFormat';
import './inbox.css';

// Sales inbox: website inquiries and phone inquiries entered by staff. Only users with
// the Sales permission (Accounts > edit user) see this page; the API enforces it too.

const VIEWS = [
  { key: 'open', label: 'Open', params: { status: 'open' } },
  { key: 'new', label: 'New', params: { status: 'new' } },
  { key: 'mine', label: 'Mine', params: { status: 'open', assigned: 'me' } },
  { key: 'closed', label: 'Won / Lost', params: { status: 'closed' } },
  { key: 'all', label: 'All', params: { status: 'all' } },
];

function Inbox() {
  const { user } = useUserContext();
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(localStorage.getItem('authToken')));
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('inquiry')) || null;

  const [view, setView] = useState('open');
  const [kind, setKind] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [dueFollowUps, setDueFollowUps] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isSales = Boolean(user && user.is_sales_advanced);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!isSales) return;
    const params = { ...VIEWS.find((v) => v.key === view).params, page };
    if (kind) params.kind = kind;
    if (debouncedQuery) params.q = debouncedQuery;
    let cancelled = false;
    fetchInquiries(params)
      .then((data) => { if (!cancelled) { setList(data); setError(''); } })
      .catch(() => { if (!cancelled) setError('Could not load the inbox.'); });
    return () => { cancelled = true; };
  }, [isSales, view, kind, debouncedQuery, page, refreshKey]);

  useEffect(() => {
    if (!isSales) return;
    fetchCrmSummary().then(setSummary).catch(() => {});
    fetchFollowUps({ due: 1, mine: 1 }).then(setDueFollowUps).catch(() => {});
  }, [isSales, refreshKey]);

  const openInquiry = useCallback((id) => {
    setSearchParams(id ? { inquiry: String(id) } : {});
  }, [setSearchParams]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const changeView = (key) => {
    setView(key);
    setPage(1);
  };

  if (!isAuthenticated) {
    return (
      <div className="App">
        <Header isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
        <div>Please log in to view content</div>
      </div>
    );
  }

  let body;
  if (!user) {
    body = <div className="inbox-empty">Loading…</div>;
  } else if (!isSales) {
    body = <div className="inbox-empty">The Inbox is for sales users. An admin can turn on Sales for you under Accounts.</div>;
  } else {
    const results = (list && list.results) || [];
    const pages = list ? Math.max(1, Math.ceil(list.count / 25)) : 1;
    body = (
      <div className={`inbox-layout ${selectedId ? 'inbox-has-selection' : ''}`}>
        <section className="inbox-list-pane">
          <div className="inbox-toolbar">
            <div className="inbox-summary">
              {summary ? (
                <>
                  <strong>{summary.new}</strong> new · <strong>{summary.open}</strong> open
                  {summary.my_follow_ups_due > 0 && <> · <strong>{summary.my_follow_ups_due}</strong> follow-up{summary.my_follow_ups_due === 1 ? '' : 's'} due</>}
                </>
              ) : ' '}
            </div>
            <button type="button" className="inbox-button inbox-button-primary" onClick={() => setShowNewForm(true)}>
              + Phone inquiry
            </button>
          </div>

          {summary && !summary.gmail.configured && (
            <div className="inbox-notice">Gmail isn't connected yet: emails can't be sent from here and replies aren't logged.</div>
          )}
          {summary && summary.gmail.configured && summary.gmail.last_error && (
            <div className="inbox-notice inbox-notice-error">Gmail sync problem: {summary.gmail.last_error}</div>
          )}

          {dueFollowUps.length > 0 && (
            <div className="inbox-due">
              <div className="inbox-section-title">Your follow-ups due</div>
              {dueFollowUps.map((f) => (
                <button key={f.id} type="button" className="inbox-due-item" onClick={() => f.inquiry && openInquiry(f.inquiry)}>
                  <span className="inbox-due-date">{formatShortDate(f.due_date)}</span>
                  <span>{f.note}</span>
                  <span className="inbox-muted">{f.inquiry_name || f.customer_name}</span>
                </button>
              ))}
            </div>
          )}

          <div className="inbox-tabs">
            {VIEWS.map((v) => (
              <button key={v.key} type="button" className={`inbox-tab ${view === v.key ? 'active' : ''}`} onClick={() => changeView(v.key)}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="inbox-filters">
            <input
              type="search"
              placeholder="Search name, company, email, phone"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            />
            <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}>
              <option value="">All types</option>
              {KIND_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>

          {error && <div className="inbox-notice inbox-notice-error">{error}</div>}
          {list && results.length === 0 && <div className="inbox-empty">Nothing here.</div>}

          <ul className="inbox-list">
            {results.map((inq) => (
              <li key={inq.id}>
                <button
                  type="button"
                  className={`inbox-row ${selectedId === inq.id ? 'selected' : ''} ${inq.status === 'new' ? 'unread' : ''}`}
                  onClick={() => openInquiry(inq.id)}
                >
                  <div className="inbox-row-top">
                    <span className="inbox-row-name">{inq.display_name}</span>
                    <span className="inbox-muted">{formatShortDate(inq.created_at)}</span>
                  </div>
                  <div className="inbox-row-bottom">
                    <span className={`inbox-status ${statusClass(inq.status)}`}>{inq.status_label}</span>
                    <span className="inbox-kind">{inq.kind_label}</span>
                    {inq.company_name && <span className="inbox-muted">{inq.contact_name}</span>}
                    {inq.attachment_count > 0 && <span className="inbox-muted">📎 {inq.attachment_count}</span>}
                    {inq.next_follow_up && <span className="inbox-muted">⏰ {formatShortDate(inq.next_follow_up)}</span>}
                    {inq.assigned_to_detail && <span className="inbox-assignee">{inq.assigned_to_detail.initials || inq.assigned_to_detail.name}</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {pages > 1 && (
            <div className="inbox-pages">
              <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Prev</button>
              <span>Page {page} of {pages}</span>
              <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next ›</button>
            </div>
          )}
        </section>

        <section className="inbox-detail-pane">
          {selectedId ? (
            <InquiryDetail
              key={selectedId}
              inquiryId={selectedId}
              currentUser={user}
              gmailConfigured={Boolean(summary && summary.gmail.configured)}
              onBack={() => openInquiry(null)}
              onChanged={refresh}
            />
          ) : (
            <div className="inbox-empty inbox-detail-placeholder">Choose an inquiry to see it here.</div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="App">
      <Header isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
      <div className="inbox-page">{body}</div>
      {showNewForm && (
        <NewInquiryForm
          onClose={() => setShowNewForm(false)}
          onCreated={(inquiry) => { setShowNewForm(false); refresh(); openInquiry(inquiry.id); }}
        />
      )}
      <PageMenu className="bottom-nav" />
    </div>
  );
}

export default Inbox;
